// What visitors do, for GA4 (guide: reports/ga4-user-behaviour-guide.md). No personal data, only what was
// clicked or searched and where.
//   content_click    a game or Glamzy look card: content_type, item, section (the row's heading), position, page_type
//   category_click   a link to a category: category, area, page_type
//   nav_click        any other link in the header, side menu or footer: area, target, page_type
//   section_view     a row came on screen, once per page: section, page_type
//   content_open     a game or look was opened: content_type, item, genre, source
//   content_time     still there after 30 s, 1, 3, 5, 10 min with the tab in front: the same, plus seconds
//   search           a search someone stopped typing: search_term, results
//   missing_search   the same, with no results (a game people want that the site does not have)

const MILESTONES = [30, 60, 180, 300, 600];
const TICK_S = 5;
const SEARCH_SETTLE_MS = 1200;

// The tag loads after the page does, so an event from a page's first moment waits for it (up to 15 s)
const waiting = [];
let waitTimer = 0;
let waitTries = 0;

export function gaEvent(name, params) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag === "function") {
    window.gtag("event", name, params);
    return;
  }
  waiting.push([name, params]);
  if (waitTimer) return;
  waitTries = 0;
  waitTimer = setInterval(() => {
    waitTries++;
    if (typeof window.gtag === "function") waiting.splice(0).forEach(([n, p]) => window.gtag("event", n, p));
    else if (waitTries < 30) return;
    else waiting.length = 0;
    clearInterval(waitTimer);
    waitTimer = 0;
  }, 500);
}

export function pageType(path = window.location.pathname) {
  if (path === "/") return "home";
  const first = path.split("/")[1];
  if (first === "game") return "game";
  if (first === "categories") return "category";
  if (first === "glamzy") return path.split("/").filter(Boolean).length > 1 ? "look" : "glamzy";
  return first || "other";
}

const clean = (text) => (text || "").replace(/\s+/g, " ").trim().slice(0, 80);

// The row or block an element sits in: its data-section (home rows), else the nearest heading going up the page
function sectionOf(el) {
  const row = el.closest("[data-section]");
  if (row) return { name: clean(row.dataset.section), box: row };
  let box = el.parentElement;
  for (let up = 0; box && box !== document.body && up < 8; up++, box = box.parentElement) {
    const heading = box.querySelector("h1, h2, h3");
    if (heading && !heading.closest("a")) return { name: clean(heading.textContent), box };
  }
  return { name: "(none)", box: el.parentElement };
}

function onClick(event) {
  const link = event.target.closest && event.target.closest("a[href]");
  if (!link) return;
  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin) return;
  const parts = url.pathname.split("/").filter(Boolean);
  const page_type = pageType();
  const area = (link.closest("[data-area]") || {}).dataset?.area || (link.closest("nav") ? "header" : link.closest("footer") ? "footer" : "page");

  const isGame = parts[0] === "game" && parts[1];
  const isLook = parts[0] === "glamzy" && parts[1] && parts[1] !== "my-lookbook";
  if ((isGame || isLook) && area !== "header") {
    const { name, box } = sectionOf(link);
    const cards = [...box.querySelectorAll('a[href^="/game/"], a[href^="/glamzy/"]')];
    gaEvent("content_click", {
      content_type: isGame ? "game" : "look", item: parts[1], section: name, position: cards.indexOf(link) + 1, page_type,
    });
    return;
  }
  if (parts[0] === "categories" && parts[1]) {
    gaEvent("category_click", { category: parts[1], area, page_type });
    return;
  }
  if (area !== "page") gaEvent("nav_click", { area, target: url.pathname, page_type });
}

// section_view: each heading on the page, once, when it first comes on screen
let observer = null;
let seenSections = new Set();
let scanTimers = [];

function scanSections() {
  if (!observer) return;
  document.querySelectorAll("main h2, main h3, h2").forEach((h) => { if (!h.dataset.gaSeen) { h.dataset.gaSeen = "1"; observer.observe(h); } });
}

export function watchSections() {
  if (typeof IntersectionObserver === "undefined") return;
  if (observer) observer.disconnect();
  scanTimers.forEach(clearTimeout);
  seenSections = new Set();
  document.querySelectorAll("[data-ga-seen]").forEach((h) => { delete h.dataset.gaSeen; });
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      const section = clean(entry.target.textContent);
      if (!section || seenSections.has(section)) return;
      seenSections.add(section);
      gaEvent("section_view", { section, page_type: pageType() });
    });
  }, { threshold: 0.5 });
  // rows that appear later (personal rows, lazy parts) are picked up by the second look
  scanTimers = [setTimeout(scanSections, 800), setTimeout(scanSections, 4000)];
}

// Called once, by pages/_app.js
export function startBehaviorTracking() {
  document.addEventListener("click", onClick, { capture: true, passive: true });
  watchSections();
  return () => document.removeEventListener("click", onClick, { capture: true });
}

// A game or look being played. Only time with the tab in front counts, so a forgotten tab reads as nothing.
let contentTimer = 0;

export function stopContent() {
  clearInterval(contentTimer);
  contentTimer = 0;
}

export function startContent({ type, item, genre, source }) {
  stopContent();
  const params = { content_type: type, item, genre: genre || "(none)", source: source || "(none)" };
  gaEvent("content_open", params);
  let seconds = 0;
  let next = 0;
  contentTimer = setInterval(() => {
    if (document.visibilityState !== "visible") return;
    seconds += TICK_S;
    if (next < MILESTONES.length && seconds >= MILESTONES[next]) {
      gaEvent("content_time", { ...params, seconds: MILESTONES[next] });
      next++;
      if (next === MILESTONES.length) stopContent();
    }
  }, TICK_S * 1000);
}

// A game's genre and where it comes from, for content_open: its first real tag, and exclusive or partner
const NOT_GENRE = new Set(["exclusive", "html5", "new", "hot", "girls games", "girl games"]);

export function gameFacts(game) {
  const tags = (game.tag || []).map((t) => String(t).toLowerCase().replace(/-/g, " ").trim());
  return {
    genre: tags.find((t) => !NOT_GENRE.has(t)) || "(none)",
    source: tags.includes("exclusive") ? "exclusive" : "partner",
  };
}

// Searches: once, when typing has stopped, not for every letter. Each word once per visit.
let searchTimer = 0;
const searched = new Set();

export function trackSearch(term, results) {
  clearTimeout(searchTimer);
  const query = clean(term).toLowerCase();
  if (query.length < 2) return;
  searchTimer = setTimeout(() => {
    if (searched.has(query)) return;
    searched.add(query);
    gaEvent("search", { search_term: query, results: results() });
    if (results() === 0) gaEvent("missing_search", { search_term: query, search_query: query });
  }, SEARCH_SETTLE_MS);
}
